import React from 'react';
import { Box, Link, Typography } from '@mui/material';
import { LegalPageLayout, LegalSection } from 'components/LegalPageLayout';
import BulletList from 'components/BulletList';
import TwoColumnTable from 'components/TwoColumnTable';

// ==============================|| DATA COLLECTION NOTICE ||============================== //
// Source: KiT Contact Data Collection Notice, Sterling Systems Private Limited,
// Version 1.0, effective/last updated 19 July 2026.
// NOTE: the source's "Privacy Policy" link target [PRIVACY POLICY URL] isn't
// explicitly confirmed anywhere — inferred here as /privacy-policy to match
// the kebab-case pattern of the other routes (/terms-and-conditions,
// /data-collection-policy, /data-retention-policy). Confirm/adjust if the
// real route differs. The retention-policy link, by contrast, is a real
// confirmed route (/data-retention-policy, from the site footer).

const DATA_COLLECTED = [
  {
    label: 'Account and identity data',
    description:
      'name, mobile number, email address, organisation or business name, login credentials or authentication identifiers.'
  },
  {
    label: 'Contact data imported or entered by the user',
    description:
      'names, telephone numbers, email addresses, addresses, organisation details, labels, notes and other contact fields selected by the user.'
  },
  {
    label: 'Relationship and activity data',
    description:
      'notes, tags, reminders, follow-up dates, interaction history and other information voluntarily entered by the user.'
  },
  {
    label: 'Device and technical data',
    description: 'device model, operating system, app version, IP address, language, diagnostic logs, crash information and security events.'
  },
  {
    label: 'Subscription and transaction data',
    description:
      'plan, subscription status, invoices, transaction reference and payment status. Complete card or banking credentials are normally processed by the payment service provider and are not intended to be stored by us.'
  },
  {
    label: 'Support communications',
    description: 'messages, attachments and information submitted when contacting support.'
  }
];

const PURPOSES = [
  {
    label: 'Account and authentication',
    description: 'Create the account, verify access, prevent misuse and communicate service information.'
  },
  {
    label: 'Core product functionality',
    description:
      'Provide the user-selected organisation, catalogue, caller, contact, reminder, synchronisation, backup and export features.'
  },
  {
    label: 'Permissions',
    description: 'Use a device capability only for the feature explained in the permission prompt.'
  },
  {
    label: 'Billing',
    description: 'Manage subscriptions, invoices, payment status, refunds and statutory records.'
  },
  {
    label: 'Security and support',
    description: 'Diagnose faults, prevent abuse, investigate incidents and answer support requests.'
  },
  {
    label: 'Legal compliance',
    description: 'Comply with binding law, court orders and lawful governmental requests; establish or defend legal claims.'
  }
];

const RIGHTS_AND_CHOICES = [
  'Access available account information and request the statutory summary where applicable.',
  'Correct, complete or update information.',
  'Request erasure, subject to lawful retention.',
  'Withdraw consent and revoke optional device permissions.',
  'Nominate a person to exercise applicable rights in the event of death or incapacity.',
  'Raise a grievance through the contact below and use statutory remedies after exhausting our grievance process.'
];

const DataCollectionNotice = () => (
  <LegalPageLayout title="Data Collection Notice" lastUpdated="19 July 2026">
    <LegalSection title="Notice at the Point of Collection">
      Sterling Systems Private Limited provides KiT Contact. Before you submit personal data or enable a device
      permission, please read this notice. It summarises what is collected, why it is needed, who may receive it, how
      long it is kept, and how to exercise your choices. The full Privacy Policy remains controlling and is available
      at <Link href="/privacy-policy">our Privacy Policy page</Link>.
    </LegalSection>

    <LegalSection title="1. Data Collected">
      <TwoColumnTable rows={DATA_COLLECTED} />
    </LegalSection>

    <LegalSection title="2. Purposes">
      <TwoColumnTable rows={PURPOSES} />
    </LegalSection>

    <LegalSection title="3. Is Submission Mandatory?">
      Data identified as required in the interface is necessary to create the account or provide the selected
      feature. Optional fields and permissions may be declined, although the related feature may not work. You may
      withdraw consent for optional processing through account or device settings, or by contacting us.
    </LegalSection>

    <LegalSection title="4. Device Permission Disclosure">
      Depending on the platform and features selected, KiT Contact may request access to contacts, camera or
      storage/media for importing, scanning, attaching or exporting information, and notifications for reminders.
      Permission access is used only for the purpose displayed at the time of request.
    </LegalSection>

    <LegalSection title="5. Sharing">
      Data may be shared with approved cloud hosting, authentication, notification, payment, analytics,
      crash-reporting and support providers strictly for service delivery; with your organisation&rsquo;s authorised
      administrator; and with authorities where legally required. We do not sell personal data.
    </LegalSection>

    <LegalSection title="6. Retention">
      Core user content is generally retained while the account remains active or until the user deletes it.
      Following account deletion, active-system deletion is normally completed within 90 days, subject to legal
      obligations, unresolved disputes, security needs and backup rotation. Billing, consent, audit and legal records
      may be retained for longer periods. See the separate{' '}
      <Link href="/data-retention-policy">Data Retention Policy</Link>.
    </LegalSection>

    <LegalSection title="7. Your Rights and Choices">
      <BulletList items={RIGHTS_AND_CHOICES} />
    </LegalSection>

    <LegalSection title="8. Contact">
      Data Fiduciary: Sterling Systems Private Limited, Pratima, First Floor, Market Yard Road, Adinanth Society,
      Maharshi Nagar, Pune, Maharashtra&ndash; 411037, INDIA.
      <br />
      Contact: <Link href="mailto:info@sterlingsys.in">info@sterlingsys.in</Link>
    </LegalSection>

    <LegalSection title="Suggested Consent Text for App / Website">
      <Box
        sx={{
          borderLeft: '3px solid',
          borderColor: 'divider',
          pl: 2,
          py: 0.5,
          fontStyle: 'italic'
        }}
      >
        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
          &ldquo;I have read the KiT Contact Data Collection Notice and Privacy Policy. I consent to the collection
          and processing of the personal data I choose to provide for account creation and the selected service
          features. I understand that optional consent and permissions may be withdrawn through settings or by
          contacting info@sterlingsys.in.&rdquo;
        </Typography>
      </Box>
    </LegalSection>
  </LegalPageLayout>
);

export default DataCollectionNotice;
